import * as React from 'react';
import styled from 'styled-components';
import IconButton from '@material-ui/core/IconButton';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { withStyles } from '@material-ui/core';

const PasswordInput = ({
  id,
  value,
  onChange,
  isValid = true,
  helperText,
  className = '',
  label = 'Password',
}: Props) => {
  const [showPassword, setShowPassword] = React.useState(
    false,
  );

  const handleShowPasswordClick = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container
      variant="outlined"
      error={!isValid}
      className={className}
    >
      <PasswordField
        id={id}
        onChange={onChange}
        value={value}
        type={showPassword ? 'text' : 'password'}
        error={!isValid}
        placeholder={label}
        endAdornment={
          <InputAdornment position="end">
            <ToggleButton
              onClick={handleShowPasswordClick}
              edge="end"
              aria-label={
                showPassword
                  ? 'Hide password'
                  : 'Show password'
              }
            >
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </ToggleButton>
          </InputAdornment>
        }
      />
      <StyledFormHelperText error={!isValid}>
        {!isValid && helperText}
      </StyledFormHelperText>
    </Container>
  );
};

interface Props {
  id: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  isValid?: boolean;
  helperText?: string | string[];
  className?: string;
  label?: string;
}

const Input = withStyles({
  root: {
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--neutral-0)',
    fontSize: 'var(--fs-base)',
    color: 'var(--neutral-800)',
    height: '40px',
    transition:
      'box-shadow var(--motion-fast) var(--ease-standard), border-color var(--motion-fast) var(--ease-standard)',

    '& input': {
      padding: '10px 12px',
      border: 0,
      outline: 0,
      background: 'transparent',
      boxShadow: 'none',
    },
    '& input:focus': {
      outline: 0,
      boxShadow: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
      display: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--neutral-300)',
      top: 0,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--neutral-400)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--primary-600)',
      borderWidth: '1px',
      boxShadow: 'var(--shadow-focus)',
    },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--danger-500)',
    },
    '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
      boxShadow: 'var(--shadow-focus-danger)',
    },
    '&.Mui-disabled': {
      backgroundColor: 'var(--neutral-100)',
      color: 'var(--neutral-500)',
    },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--neutral-200)',
    },
  },
})(OutlinedInput);

const Container = styled(FormControl)`
  width: 100%;
`;

const PasswordField = styled(Input)`
  legend > span {
    display: none;
  }
`;

const ToggleButton = styled(IconButton)`
  padding: 6px;
  margin-right: -4px;
  color: var(--neutral-500);
  transition: color var(--motion-fast) var(--ease-standard),
    background-color var(--motion-fast) var(--ease-standard);

  &:hover {
    color: var(--primary-600);
    background-color: var(--primary-50);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const StyledFormHelperText = styled(FormHelperText)`
  position: absolute;
  margin: 0;
  bottom: -18px;
  left: 14px;
  font-family: inherit;
  font-size: var(--fs-sm);

  &.Mui-error {
    color: var(--danger-600);
  }
`;

export default PasswordInput;
